// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import authService from '@/services/authService';
import { initializeUserPreferences } from '@/services/preferenceService';
import preferenceService from '@/services/preferenceService';
import lookupService, { initializeAllLookups } from '@/services/lookupService';
import logoService from '@/services/logoService';
import billingService from '@/services/billingService';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import PostLoginBillingModal from '@/features/auth/components/PostLoginBillingModal';
import ViewRevShareModal from '@/features/auth/components/ViewRevShareModal';
import ESignatureModal from '@/features/auth/registration/components/ESignatureModal';
import useManagerBillingSignatures from '@/features/auth/hooks/useManagerBillingSignatures';
import { ZINDEX } from '@/core/platform/platformStandards';

// sessionStorage key used to ensure the billing modal is only shown once per
// browser session (i.e. not again when the user refreshes the page).
export const BILLING_MODAL_SESSION_KEY = 'billing_modal_handled';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start as loading
  const [loginLoading, setLoginLoading] = useState(false); // Separate loading for login attempts
  const [error, setError] = useState(null);
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaExpiresIn, setMfaExpiresIn] = useState(0);
  
  // Billing modal state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingModalData, setBillingModalData] = useState(null);
  const [billingRevShareModalOpen, setBillingRevShareModalOpen] = useState(false);
  const [billingSubscriptionModalOpen, setBillingSubscriptionModalOpen] = useState(false);
  const [billingSubscriptionPlan, setBillingSubscriptionPlan] = useState('annual');
  const [showRevShareViewModal, setShowRevShareViewModal] = useState(false);
  const [stripeRedirecting, setStripeRedirecting] = useState(false);
  
  const queryClient = useQueryClient();
  const { selectRevShareBilling, recordSubscriptionAndCheckout } = useManagerBillingSignatures();
  
  // Check if the user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      console.log('🔍 AUTH CHECK: Starting auth check on page load');
      console.log('🔑 token available:', !!token);
      console.log('👤 userId available:', userId);
      
      if (!token) {
        console.log('No token found, setting loading to false');
        setLoading(false);
        return;
      }

      // Stripe redirects here before child routes mount; set flag synchronously so
      // we never open the billing gate modal while checkout webhook catches up.
      try {
        const qs = new URLSearchParams(window.location.search);
        if (qs.get('billing_return') === 'success') {
          sessionStorage.setItem(BILLING_MODAL_SESSION_KEY, 'true');
          setShowBillingModal(false);
          setBillingModalData(null);
        }
      } catch {
        /* ignore */
      }
      
      try {
        console.log('Checking auth status...');
        setLoading(true);
        const userData = await authService.checkAuthStatus();
        console.log('Auth check succeeded, preparing user data:', userData);
        
        let finalUserData = userData;
        
        // 🚀 PARALLEL LOADING: Load essential data simultaneously on refresh
        console.log('🔄 Starting parallel data loading on refresh...');
        const parallelStartTime = performance.now();

        const parallelTasks = [];
        let logoTask = null;
        
        // 1. Firm logo loading (for managers)
        if (userData && userData.role === 'manager' && userData.firm?.firm_id) {
          console.log('🖼️ Starting firm logo loading on refresh...');
          logoTask = logoService.getLogo('firm', userData.firm.firm_id)
            .then(logoResponse => {
              if (logoResponse.status === 'success' && logoResponse.image) {
                console.log('✅ Firm logo loaded on refresh');
                return logoResponse.image;
              } else {
                console.log('📸 No firm logo available on refresh:', logoResponse.message);
                return null;
              }
            })
            .catch(logoError => {
              console.error('❌ Failed to load firm logo on refresh:', logoError);
              return null;
            });
          parallelTasks.push(logoTask);
        } else {
          console.log('⏭️ Firm logo loading skipped on refresh');
        }

        // 2. Lookups initialization
        console.log('📚 Starting lookups initialization on refresh...');
        const lookupsTask = initializeAllLookups()
          .then(() => {
            console.log('✅ Lookups initialized successfully on refresh');
            return true;
          })
          .catch(lookupError => {
            console.error('❌ Lookups initialization failed on refresh:', lookupError);
            return false;
          });
        parallelTasks.push(lookupsTask);

        // 3. User preferences loading
        let preferencesTask = null;
        if (userData && (userData.role === 'investor' || userData.role === 'manager')) {
          console.log(`⚙️ Starting ${userData.role} preferences loading on refresh...`);
          preferencesTask = initializeUserPreferences(userData.user_id || userData.userId, userData.role)
            .then(() => {
              console.log(`✅ ${userData.role} preferences loaded successfully on refresh`);
              return true;
            })
            .catch(prefError => {
              console.warn(`❌ Failed to load ${userData.role} preferences on refresh:`, prefError);
              return false;
            });
          parallelTasks.push(preferencesTask);
        } else {
          console.log('⏭️ Preferences loading skipped on refresh');
        }

        // Execute all tasks in parallel
        try {
          const results = await Promise.all(parallelTasks);
          let logoUrl = null;
          let resultIndex = 0;
          
          // Process results
          if (logoTask) {
            logoUrl = results[resultIndex];
            resultIndex++;
          }
          const lookupsSuccess = results[resultIndex];
          resultIndex++;
          let preferencesSuccess = true;
          if (preferencesTask) {
            preferencesSuccess = results[resultIndex];
          }

          // Update user data with logo if available
          if (logoUrl && userData.role === 'manager') {
            finalUserData = {
              ...userData,
              firm: {
                ...userData.firm,
                logo_url: logoUrl
              }
            };
          }

          const parallelTime = performance.now() - parallelStartTime;
          console.log(`✅ Parallel data loading completed on refresh (${parallelTime.toFixed(0)}ms)`);
          console.log(`📊 Refresh results: Logo=${!!logoUrl}, Lookups=${lookupsSuccess}, Preferences=${preferencesSuccess}`);
          
        } catch (parallelError) {
          console.error('❌ Parallel data loading failed on refresh:', parallelError);
          // Continue with auth check even if some data fails to load
        }

        // Manager billing gate: DashboardContainer → PostLoginAgreementModal only.
        // Backend profile + standardizeUserFormat align requires_billing_selection with login.

        setUser(finalUserData);
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('hfrconnect_registration');
        sessionStorage.removeItem('hfrconnect_registration_session');
        
        setError('Session expired. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  const finalizeAuthenticatedSession = useCallback(async (result) => {
    let finalUserData = result.user;

    const parallelTasks = [];
    let logoTask = null;
    if (result.user && result.user.role === 'manager' && result.user.firm?.firm_id) {
      logoTask = logoService.getLogo('firm', result.user.firm.firm_id)
        .then((logoResponse) => {
          if (logoResponse.status === 'success' && logoResponse.image) {
            return logoResponse.image;
          }
          return null;
        })
        .catch(() => null);
      parallelTasks.push(logoTask);
    }

    const lookupsTask = initializeAllLookups()
      .then(() => true)
      .catch(() => false);
    parallelTasks.push(lookupsTask);

    let preferencesTask = null;
    if (result.user && (result.user.role === 'investor' || result.user.role === 'manager')) {
      preferencesTask = initializeUserPreferences(
        result.user.user_id || result.user.userId,
        result.user.role
      )
        .then(() => true)
        .catch(() => false);
      parallelTasks.push(preferencesTask);
    }

    try {
      const results = await Promise.all(parallelTasks);
      let i = 0;
      let logoUrl = null;
      if (logoTask) {
        logoUrl = results[i];
        i += 1;
      }
      i += 1; // lookupsTask
      if (preferencesTask) {
        i += 1;
      }
      if (logoUrl && result.user.role === 'manager') {
        finalUserData = {
          ...result.user,
          firm: {
            ...result.user.firm,
            logo_url: logoUrl
          }
        };
      }
    } catch (parallelError) {
      console.error('Parallel loading failed:', parallelError);
    }

    setUser(finalUserData);
    setMfaRequired(false);
    queryClient.clear();

    return finalUserData;
  }, [queryClient]);

  // Wrap functions with useCallback to stabilize their references
  const login = useCallback(async (email, password, recaptchaToken = null) => {
    const loginStartTime = performance.now();
    console.log('🚀 LOGIN PROCESS START');
    console.log('🔐 AuthContext received recaptchaToken:', {
      provided: recaptchaToken !== null && recaptchaToken !== undefined,
      type: typeof recaptchaToken,
      length: recaptchaToken?.length
    });
    
    setLoginLoading(true); // Use separate login loading state
    setError(null);
    
    try {
      console.log('📡 Step 1: Calling login API...');
      const apiStartTime = performance.now();
      const result = await authService.login(email, password, recaptchaToken);
      console.log(`✅ Step 1 complete: Login API (${(performance.now() - apiStartTime).toFixed(0)}ms)`);
      console.log('👤 User data received:', { role: result.user?.role, hasLogo: result.user?.firm?.has_logo });
      
      // Check if MFA is required
      if (result.mfa_required) {
        console.log('MFA required, setting MFA state');
        setMfaRequired(true);
        setMfaMessage(result.message);
        setMfaExpiresIn(result.expires_in_minutes);
        
        return { mfa_required: true, message: result.message };
      } else {
        console.log('Login succeeded, preparing user data:', result.user);
        const user = await finalizeAuthenticatedSession(result);
        const totalLoginTime = performance.now() - loginStartTime;
        console.log(`🏁 LOGIN PROCESS COMPLETE: Total time ${totalLoginTime.toFixed(0)}ms`);
        return user;
      }
    } catch (err) {
      // Don't set global error for login failures - let the Login component handle it
      // Only set global errors for system-level issues, not user credential errors
      console.error('Login failed:', err);
      setMfaRequired(false); // Clear MFA state on error
      throw err; // Throw the error so the Login component can handle it
    } finally {
      setLoginLoading(false); // Use separate login loading state
    }
  }, [finalizeAuthenticatedSession]);

  const loginWithMagicLink = useCallback(
    async (email, session, code, recaptchaToken = null) => {
      setLoginLoading(true);
      setError(null);
      try {
        const result = await authService.verifyMagicLink(email, session, code, recaptchaToken);
        return await finalizeAuthenticatedSession(result);
      } catch (err) {
        console.error('Magic link login failed:', err);
        setMfaRequired(false);
        throw err;
      } finally {
        setLoginLoading(false);
      }
    },
    [finalizeAuthenticatedSession]
  );

  const finalizeRegistrationSession = useCallback(
    async (registrationResponse) => {
      setLoginLoading(true);
      setError(null);
      try {
        authService.persistTokensFromRegistrationResponse(registrationResponse);
        const userData = await authService.checkAuthStatus();
        await finalizeAuthenticatedSession({ user: userData });
        return userData;
      } finally {
        setLoginLoading(false);
      }
    },
    [finalizeAuthenticatedSession]
  );

  // Verify MFA code
  const verifyMFA = useCallback(async (mfaCode) => {
    setLoginLoading(true); // Use separate login loading state for MFA too
    setError(null);
    
    try {
      const result = await authService.verifyMFA(mfaCode);
      
      if (result.mfa_verified) {
        console.log('MFA verified successfully');
        setMfaRequired(false);
        setMfaMessage('');
        setMfaExpiresIn(0);
        
        // MFA verification successful, now complete the login process
        // We need to re-authenticate to get tokens and user data
        // For now, return success and let frontend handle re-login
        return { mfa_verified: true, message: result.message };
      } else {
        throw new Error('MFA verification failed');
      }
    } catch (err) {
      console.error('MFA verification failed:', err);
      throw err;
    } finally {
      setLoginLoading(false); // Use separate login loading state for MFA too
    }
  }, []);

  // Clear MFA state
  const clearMFA = useCallback(() => {
    setMfaRequired(false);
    setMfaMessage('');
    setMfaExpiresIn(0);
    // Clear session storage
    sessionStorage.removeItem('mfa_token');
    sessionStorage.removeItem('mfa_user_id');
    sessionStorage.removeItem('mfa_expires_at');
  }, []);
  
  const logout = useCallback(async () => {
    try {
      setLoginLoading(true); // Use separate login loading state for logout too
      await authService.logout();
      setUser(null);
      
      queryClient.clear();
      
      // Clear preferences and lookup cache on logout
      preferenceService.clearCache();
      lookupService.clearCache();
      
      // Clear registration-related storage
      localStorage.removeItem('hfrconnect_registration');
      sessionStorage.removeItem('hfrconnect_registration_session');
      
        // Clear explore opportunities state for security and UX
        sessionStorage.removeItem('exploreOpportunities_viewMode');
        sessionStorage.removeItem('exploreV2_viewMode');
        sessionStorage.removeItem('exploreV2_viewUserTouched');
        sessionStorage.removeItem('exploreV2_sort_state');
        sessionStorage.removeItem('exploreV2_sort');
        sessionStorage.removeItem('exploreV2_lenses');
        sessionStorage.removeItem('exploreOpportunities_filters');
        sessionStorage.removeItem('exploreOpportunities_page');
        sessionStorage.removeItem('exploreOpportunities_search');
        sessionStorage.removeItem('exploreOpportunities_gridView');
        sessionStorage.removeItem('exploreOpportunities_metricsExpanded');
        sessionStorage.removeItem('exploreOpportunities_scrollPosition');
        
        // Clear dashboard initial load flag to show animations on next login
      sessionStorage.removeItem('dashboard_loaded');
      
      // Clear theme state for fresh preference loading on next login
      localStorage.removeItem('themeMode');
      sessionStorage.removeItem('theme_session_active');

      // Reset billing modal session flag so it can appear again on next login
      sessionStorage.removeItem(BILLING_MODAL_SESSION_KEY);
    } catch (err) {
      console.error('Logout error:', err);
      
      setUser(null);
      queryClient.clear();
      
      // Clear preferences and lookup cache even on logout error
      preferenceService.clearCache();
      lookupService.clearCache();
      
        // Clear storage even on error
        localStorage.removeItem('hfrconnect_registration');
        sessionStorage.removeItem('hfrconnect_registration_session');
        sessionStorage.removeItem('exploreOpportunities_viewMode');
        sessionStorage.removeItem('exploreV2_viewMode');
        sessionStorage.removeItem('exploreV2_viewUserTouched');
        sessionStorage.removeItem('exploreV2_sort_state');
        sessionStorage.removeItem('exploreV2_sort');
        sessionStorage.removeItem('exploreV2_lenses');
        sessionStorage.removeItem('exploreOpportunities_filters');
        sessionStorage.removeItem('exploreOpportunities_page');
        sessionStorage.removeItem('exploreOpportunities_search');
        sessionStorage.removeItem('exploreOpportunities_gridView');
        sessionStorage.removeItem('exploreOpportunities_metricsExpanded');
        sessionStorage.removeItem('exploreOpportunities_scrollPosition');
      
      // Clear dashboard initial load flag to show animations on next login
      sessionStorage.removeItem('dashboard_loaded');
      
      // Clear theme state for fresh preference loading on next login
      localStorage.removeItem('themeMode');
      sessionStorage.removeItem('theme_session_active');

      // Reset billing modal session flag so it can appear again on next login
      sessionStorage.removeItem(BILLING_MODAL_SESSION_KEY);
    } finally {
      setLoginLoading(false); // Use separate login loading state for logout too
    }
  }, [queryClient]);
  
  const register = useCallback(async (userData) => {
    setLoginLoading(true); // Use separate login loading state for registration too
    setError(null);
    
    try {
      const result = await authService.register(userData);
      setUser(result.user);
      
      queryClient.clear();
      
      localStorage.removeItem('hfrconnect_registration');
      sessionStorage.removeItem('hfrconnect_registration_session');
      
      return result.user;
    } catch (err) {
      // Don't set global error for registration failures - let the Registration component handle it
      // Only set global errors for system-level issues, not user input errors
      console.error('Registration failed:', err);
      throw err; // Throw the error so the Registration component can handle it
    } finally {
      setLoginLoading(false); // Use separate login loading state for registration too
    }
  }, [queryClient]);
  
  const clearError = useCallback(() => setError(null), []);

  // Refresh user data with parallel loading pattern
  const refreshUserData = useCallback(async () => {
    console.log('🔄 REFRESH USER DATA START');
    const refreshStartTime = performance.now();

    try {
      // Get fresh user data first
      console.log('👤 Step 1: Getting fresh user data...');
      const userData = await authService.checkAuthStatus();
      console.log('✅ Fresh user data received:', userData);

      let finalUserData = userData;

      // 🚀 PARALLEL LOADING: Load essential data simultaneously
      console.log('🔄 Step 2: Loading essential data in parallel...');
      const parallelStartTime = performance.now();

      const parallelTasks = [];
      let logoTask = null;

      // Task 1: Load firm logo (if needed)
      if (userData && userData.role === 'manager' && userData.firm?.firm_id) {
        console.log('🖼️ Starting firm logo loading...');
        logoTask = logoService.getLogo('firm', userData.firm.firm_id)
          .then(logoResponse => {
            if (logoResponse.status === 'success' && logoResponse.image) {
              console.log('✅ Firm logo loaded successfully');
              return logoResponse.image;
            } else {
              console.log('⚠️ Firm logo not available');
              return null;
            }
          })
          .catch(logoError => {
            console.warn('⚠️ Firm logo loading failed:', logoError);
            return null;
          });
        parallelTasks.push(logoTask);
      } else {
        console.log('⏭️ Firm logo loading skipped');
      }

      // Task 2: Initialize lookups
      console.log('📚 Starting lookups initialization...');
      const lookupsTask = initializeAllLookups()
        .then(() => {
          console.log('✅ Lookups initialized successfully');
          return true;
        })
        .catch(lookupError => {
          console.error('❌ Lookups initialization failed:', lookupError);
          return false;
        });
      parallelTasks.push(lookupsTask);

      // Task 3: Load user preferences (if needed)
      let preferencesTask = null;
      if (userData && (userData.role === 'investor' || userData.role === 'manager')) {
        console.log(`⚙️ Starting ${userData.role} preferences loading...`);
        preferencesTask = initializeUserPreferences(userData.user_id || userData.userId, userData.role)
          .then(() => {
            console.log(`✅ ${userData.role} preferences loaded successfully`);
            return true;
          })
          .catch(prefError => {
            console.warn(`❌ ${userData.role} preferences loading failed:`, prefError);
            return false;
          });
        parallelTasks.push(preferencesTask);
      } else {
        console.log('⏭️ Preferences loading skipped');
      }

      // Wait for all parallel tasks to complete
      try {
        const results = await Promise.all(parallelTasks);
        
        // Process results
        let logoUrl = null;
        let resultIndex = 0;
        
        if (logoTask) {
          logoUrl = results[resultIndex];
          resultIndex++;
        }
        
        const lookupsSuccess = results[resultIndex];
        resultIndex++;
        
        let preferencesSuccess = true;
        if (preferencesTask) {
          preferencesSuccess = results[resultIndex];
        }
        
        // Update user data with logo if loaded
        if (logoUrl && userData.role === 'manager') {
          finalUserData = {
            ...userData,
            firm: {
              ...userData.firm,
              logo_url: logoUrl,
              has_logo: true
            }
          };
        }
        
        const parallelTime = performance.now() - parallelStartTime;
        console.log(`✅ Step 2 complete: All essential data loaded in parallel (${parallelTime.toFixed(0)}ms)`);
        console.log(`📊 Parallel results: Logo=${!!logoUrl}, Lookups=${lookupsSuccess}, Preferences=${preferencesSuccess}`);
        
      } catch (parallelError) {
        console.error('❌ Parallel loading failed:', parallelError);
        // Continue even if some parallel tasks fail
      }

      // Set user state once with complete data
      console.log('👤 Step 3: Setting user state with all data loaded...');
      const userStateStartTime = performance.now();
      setUser(finalUserData);
      queryClient.clear();
      console.log(`✅ Step 3 complete: User state set (${(performance.now() - userStateStartTime).toFixed(0)}ms)`);

      try {
        if (sessionStorage.getItem(BILLING_MODAL_SESSION_KEY)) {
          setShowBillingModal(false);
          setBillingModalData(null);
        }
      } catch {
        /* ignore */
      }

      const totalRefreshTime = performance.now() - refreshStartTime;
      console.log(`🏁 REFRESH USER DATA COMPLETE: Total time ${totalRefreshTime.toFixed(0)}ms`);

      return finalUserData;
    } catch (err) {
      console.error('❌ Refresh user data failed:', err);
      throw err;
    }
  }, [queryClient]);

  // Utility to get API-ready user info from the current user
  const getUserInfoForApi = useCallback(() => {
    // Remove unnecessary logging
    if (!user?.userId && !user?.user_id) {
      return null;
    }
    
    // Use whichever ID is available (prefer camelCase if both exist)
    const id = user.userId || user.user_id;
    
    const userInfo = {
      user_id: id,         // Ensure snake_case version exists for API
      userId: id,          // Ensure camelCase version exists for frontend
      user_type: user.role || user.user_type || 'investor',  // Handle both naming conventions
      userType: user.role || user.user_type || 'investor',   // Handle both naming conventions
      user_name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User',
      userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User',
      user_email: user.email || user.userEmail || '',
      userEmail: user.email || user.userEmail || '',
      institution_type: user.institution_type || user.institutionType || '',  // Add institution type for filtering
      firm_id: user.manager_firm_id || user.firmId || null,  // Firm ID for managers (null for investors/admins)
      firmId: user.manager_firm_id || user.firmId || null    // Firm ID for managers (camelCase)
    };
    
    return userInfo;
  }, [user]);

  // Update user agreement status after post-login acceptance
  const updateUserAgreementStatus = useCallback((agreementData) => {
    setUser(prevUser => ({
      ...prevUser,
      agreement_accepted: agreementData.agreement_accepted,
      agreement_version: agreementData.agreement_version,
      agreement_accepted_at: agreementData.agreement_accepted_at
      // Note: rev_share_signature is stored in rev_share_master (firm-level), not user-level
    }));
  }, []);
  
  const openBillingModal = useCallback((data) => {
    setBillingModalData(data);
    setShowBillingModal(true);
  }, []);

  const openRevShareSign = useCallback((data) => {
    setBillingModalData(
      data || {
        firmId: user?.firm?.firm_id,
        userId: user?.user_id || user?.userId,
      }
    );
    setBillingRevShareModalOpen(true);
  }, [user]);

  const patchFirmSubscription = useCallback((syncData) => {
    if (!syncData) return;
    setUser((prev) => {
      if (!prev) return prev;
      const firmPatch = {};
      if (syncData.subscription !== undefined) firmPatch.subscription = syncData.subscription;
      if (syncData.firm_billing_status !== undefined) firmPatch.firm_billing_status = syncData.firm_billing_status;
      if (syncData.firm_subscription_period_end !== undefined) firmPatch.firm_subscription_period_end = syncData.firm_subscription_period_end;
      if (syncData.firm_subscription_cancel_at_period_end !== undefined) firmPatch.firm_subscription_cancel_at_period_end = syncData.firm_subscription_cancel_at_period_end;
      return {
        ...prev,
        firm: { ...(prev.firm || {}), ...firmPatch },
      };
    });
  }, []);

  const patchFirmLogo = useCallback((logoDataUrl) => {
    setUser((prev) => {
      if (!prev) return prev;
      const role = prev.role || prev.user_type;
      if (role !== 'manager' || !prev.firm) return prev;
      if (logoDataUrl) {
        return {
          ...prev,
          firm: { ...prev.firm, logo_url: logoDataUrl, has_logo: true },
        };
      }
      return {
        ...prev,
        firm: { ...prev.firm, logo_url: null, has_logo: false },
      };
    });
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    loginLoading,
    error,
    login,
    loginWithMagicLink,
    finalizeRegistrationSession,
    logout,
    register,
    isAuthenticated: !!user,
    clearError,
    getUserInfoForApi,
    mfaRequired,
    mfaMessage,
    mfaExpiresIn,
    verifyMFA,
    clearMFA,
    refreshUserData,
    updateUserAgreementStatus,
    showBillingModal,
    setShowBillingModal,
    openBillingModal,
    openRevShareSign,
    patchFirmSubscription,
    patchFirmLogo,
  }), [user, loading, loginLoading, error, login, loginWithMagicLink, finalizeRegistrationSession, logout, register, clearError, getUserInfoForApi, mfaRequired, mfaMessage, mfaExpiresIn, verifyMFA, clearMFA, refreshUserData, updateUserAgreementStatus, showBillingModal, openBillingModal, openRevShareSign, patchFirmSubscription, patchFirmLogo]);

  const closeBillingModal = useCallback(async (refreshData = true) => {
    setShowBillingModal(false);
    setBillingModalData(null);
    // Mark as handled so the modal is not shown again on page refresh this session.
    sessionStorage.setItem(BILLING_MODAL_SESSION_KEY, 'true');
    if (refreshData) {
      try {
        const userData = await authService.checkAuthStatus();
        setUser(userData);
      } catch (err) {
        console.error('Error refreshing user data after billing selection:', err);
      }
    }
  }, []);

  const handleSelectDefer = useCallback(async () => {
    console.log('⏳ Billing deferred, updating billing model...');
    await billingService.selectBillingModel({
      firm_id: billingModalData?.firmId,
      user_id: billingModalData?.userId,
      billing_model: 'deferred'
    });
    await closeBillingModal(true);
  }, [billingModalData, closeBillingModal]);

  const handleCancelBilling = useCallback(() => {
    console.log('ℹ️ Billing modal dismissed by user.');
    closeBillingModal(false);
  }, [closeBillingModal]);

  const billingRevShareFirmData = useMemo(() => {
    if (!user) return null;
    return {
      firmName: user?.firm?.firm_name || '',
      firmAddress: '',
      firmEmail: user?.email || ''
    };
  }, [user]);

  const billingRevShareUserData = useMemo(
    () => ({
      fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      jobTitle: user?.manager_position || ''
    }),
    [user]
  );

  const handleOpenBillingRevShareSign = useCallback(() => {
    setShowBillingModal(false);
    setBillingRevShareModalOpen(true);
  }, []);

  const handleCloseBillingRevShareSign = useCallback(() => {
    setBillingRevShareModalOpen(false);
  }, []);

  const handleBillingRevShareSigned = useCallback(
    async (signatureData) => {
      const { reportSubmitStatus, ...sig } = signatureData;
      try {
        reportSubmitStatus?.('Recording your signature and applying it to your agreement…');
        await selectRevShareBilling({
          firmId: billingModalData?.firmId,
          userId: billingModalData?.userId,
          signatureData: sig,
        });

        setBillingRevShareModalOpen(false);
        await closeBillingModal(true);
      } catch (err) {
        console.error('Rev share billing error:', err);
        throw err;
      }
    },
    [billingModalData, closeBillingModal, selectRevShareBilling]
  );

  const _handleBillingSubscribe = useCallback((plan) => {
    setBillingSubscriptionPlan(plan || 'annual');
    setShowBillingModal(false);
    setBillingSubscriptionModalOpen(true);
  }, []);

  const handleCloseBillingSubscriptionSign = useCallback(() => {
    setBillingSubscriptionModalOpen(false);
    setShowBillingModal(true);
  }, []);

  const handleBillingSubscriptionSigned = useCallback(
    async (signatureData) => {
      const { reportSubmitStatus, ...sig } = signatureData;
      try {
        reportSubmitStatus?.('Recording your signature…');
        setBillingSubscriptionModalOpen(false);
        setStripeRedirecting(true);
        await recordSubscriptionAndCheckout({
          firmId: billingModalData?.firmId,
          userId: billingModalData?.userId,
          signatureData: sig,
          plan: billingSubscriptionPlan,
        });
      } catch (err) {
        console.error('Subscription checkout error:', err);
        setStripeRedirecting(false);
        throw err;
      }
    },
    [billingModalData, billingSubscriptionPlan, recordSubscriptionAndCheckout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Billing Modal — Stripe return / rev-share sub-flows (manager gate is DashboardContainer) */}
      {showBillingModal && billingModalData && (
        <PostLoginBillingModal
          open={showBillingModal && !billingSubscriptionModalOpen}
          deferredUntil={billingModalData.deferredUntil}
          canDefer={billingModalData.canDefer}
          onSelectRevShare={handleOpenBillingRevShareSign}
          onSelectDefer={handleSelectDefer}
          onCancel={handleCancelBilling}
          onOpenRevShareView={() => setShowRevShareViewModal(true)}
        />
      )}
      <ViewRevShareModal
        open={showRevShareViewModal}
        onClose={() => setShowRevShareViewModal(false)}
      />
      {billingRevShareModalOpen && user?.role === 'manager' && (
        <ESignatureModal
          open={billingRevShareModalOpen}
          onClose={handleCloseBillingRevShareSign}
          onSign={handleBillingRevShareSigned}
          isLoading={false}
          firmData={billingRevShareFirmData}
          userData={billingRevShareUserData}
          agreementType="revenue_share"
        />
      )}
      {billingSubscriptionModalOpen && user?.role === 'manager' && (
        <ESignatureModal
          open={billingSubscriptionModalOpen}
          onClose={handleCloseBillingSubscriptionSign}
          onSign={handleBillingSubscriptionSigned}
          isLoading={false}
          firmData={billingRevShareFirmData}
          userData={billingRevShareUserData}
          agreementType="subscription"
        />
      )}
      {stripeRedirecting && (
        <Backdrop
          open
          sx={{
            zIndex: ZINDEX.modal + 1,
            flexDirection: 'column',
            gap: 2,
            color: 'common.white',
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body2" color="inherit">
            Redirecting to Stripe…
          </Typography>
        </Backdrop>
      )}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
